import IPoll, { PollEventTypeCode, PollType } from '../../model/IPoll';
import { ICreatePollRequest, IUpdatePollRequest } from '../../services/DataService';
import { toDate } from '../../lib/format';
import {
    getPollQuestions,
    IEditableQuestion,
    toCreateQuestions,
    toUpdateQuestions,
    validateQuestion,
} from '../../lib/pollQuestions';

/** Single form model used by the create wizard and the edit page. */
export interface IPollFormValues {
    title: string;
    description: string;
    eventTypeCode?: PollEventTypeCode;
    geoRegionId: string;
    geoTerritoryId: string;
    singleResponse: boolean;
    completedDisplayResults: boolean;
    completedMessage: string;
    opensAt?: Date;
    closesAt?: Date;
    questions: IEditableQuestion[];
}

export function createEmptyPollForm(): IPollFormValues {
    return {
        title: '',
        description: '',
        eventTypeCode: undefined,
        geoRegionId: '',
        geoTerritoryId: '',
        singleResponse: true,
        completedDisplayResults: true,
        completedMessage: 'Спасибо за участие!',
        opensAt: undefined,
        closesAt: undefined,
        questions: [],
    };
}

export function pollToForm(poll: IPoll): IPollFormValues {
    return {
        title: poll.Title ?? '',
        description: poll.Description ?? '',
        eventTypeCode: poll.EventTypeCode,
        geoRegionId: poll.GeoRegionId ?? '',
        geoTerritoryId: poll.GeoTerritoryId ?? '',
        singleResponse: !!poll.SingleResponse,
        completedDisplayResults: !!poll.CompletedDisplayResults,
        completedMessage: poll.CompletedMessage ?? '',
        opensAt: toDate(poll.OpensAt),
        closesAt: toDate(poll.ClosesAt),
        questions: getPollQuestions(poll),
    };
}

export interface IPollFormErrors {
    title?: string;
    eventTypeCode?: string;
    geoRegionId?: string;
    dates?: string;
    questions?: string;
    /** Per-question error keyed by question.key. */
    questionErrors: Record<string, string>;
}

export function validateDetails(values: IPollFormValues): Pick<IPollFormErrors, 'title' | 'eventTypeCode' | 'geoRegionId'> {
    return {
        title: values.title.trim() ? undefined : 'Укажите название опроса.',
        eventTypeCode: values.eventTypeCode === undefined ? 'Выберите тип мероприятия.' : undefined,
        geoRegionId: values.geoRegionId ? undefined : 'Выберите регион.',
    };
}

export function validateDates(values: IPollFormValues): string | undefined {
    if (values.opensAt && values.closesAt && values.opensAt > values.closesAt) {
        return 'Дата начала не может быть позже даты окончания.';
    }
    return undefined;
}

export function validateQuestions(questions: IEditableQuestion[], min = 1): Pick<IPollFormErrors, 'questions' | 'questionErrors'> {
    const questionErrors: Record<string, string> = {};
    questions.forEach((q) => {
        const error = validateQuestion(q);
        if (error) questionErrors[q.key] = error;
    });
    return {
        questions: questions.length < min ? 'Добавьте хотя бы один вопрос.' : undefined,
        questionErrors,
    };
}

export function validatePollForm(values: IPollFormValues): IPollFormErrors {
    return {
        ...validateDetails(values),
        dates: validateDates(values),
        ...validateQuestions(values.questions),
    };
}

export function hasErrors(errors: Partial<IPollFormErrors>): boolean {
    return !!(errors.title || errors.eventTypeCode || errors.geoRegionId || errors.dates || errors.questions
        || (errors.questionErrors && Object.keys(errors.questionErrors).length > 0));
}

export function firstError(errors: Partial<IPollFormErrors>): string | undefined {
    return errors.title ?? errors.eventTypeCode ?? errors.geoRegionId ?? errors.dates ?? errors.questions
        ?? (errors.questionErrors ? Object.values(errors.questionErrors)[0] : undefined);
}

export function toCreateRequest(values: IPollFormValues, quick: boolean): ICreatePollRequest {
    return {
        Quick: quick,
        Type: quick ? PollType.Fast : PollType.Complex,
        Title: values.title.trim(),
        Description: values.description.trim() || undefined,
        EventTypeCode: values.eventTypeCode,
        IsAnonymous: true,
        GeoRegionId: values.geoRegionId || undefined,
        GeoTerritoryId: values.geoTerritoryId || undefined,
        SingleResponse: values.singleResponse,
        OpensAt: values.opensAt ? values.opensAt.toISOString() : undefined,
        ClosesAt: values.closesAt ? values.closesAt.toISOString() : undefined,
        CompletedDisplayResults: values.completedDisplayResults,
        CompletedMessage: values.completedMessage.trim() || undefined,
        Questions: toCreateQuestions(values.questions),
    };
}

export function toUpdateRequest(values: IPollFormValues): IUpdatePollRequest {
    return {
        Title: values.title.trim(),
        Description: values.description.trim() || undefined,
        EventTypeCode: values.eventTypeCode,
        GeoRegionId: values.geoRegionId || undefined,
        GeoTerritoryId: values.geoTerritoryId || undefined,
        SingleResponse: values.singleResponse,
        OpensAt: values.opensAt ? values.opensAt.toISOString() : undefined,
        ClosesAt: values.closesAt ? values.closesAt.toISOString() : undefined,
        CompletedDisplayResults: values.completedDisplayResults,
        CompletedMessage: values.completedMessage.trim() || undefined,
        Questions: toUpdateQuestions(values.questions),
    };
}
